import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import VAPServiceEditModalErrorMessage from '@@vap-svc/components/base/VAPServiceEditModalErrorMessage';
import prefixUtilityClasses from '~/platform/utilities/prefix-utility-classes';
import {
  clearTransactionRequest,
  createTransaction,
  openModal,
  refreshTransaction,
} from '../actions';
import { createSchedulingPreferencesUpdate } from '../actions/schedulingPreferences';
import { updateMessagingSignature } from '../../actions/mhv';
import { FIELD_NAMES, FIELD_SECTION_HEADERS, FIELD_TITLES } from '../constants';
import { useFieldControllerEffects } from '../hooks/useFieldControllerEffects';
import { useProfileInformationFieldData } from '../hooks/useProfileInformationFieldData';
import { isSchedulingPreference } from '../util/health-care-settings/schedulingPreferencesUtils';
import { getEditButtonId, getRemoveButtonId } from '../util/id-factory';
import {
  DELETE_STRATEGIES,
  getDeleteStrategy,
} from '../util/profile-information-field/getDeleteStrategy';
import {
  EDIT_STRATEGIES,
  getEditStrategy,
  getRemoveStrategy,
} from '../util/profile-information-field/getEditStrategy';
import {
  getEditViewProps,
  isPrefillMailingAddress,
} from '../util/profile-information-field/getEditViewProps';
import { isPendingTransaction } from '../util/transactions';
import AddressValidationView2 from '../containers/AddressValidationView2';
import VAPServiceTransaction from './base/VAPServiceTransaction';
import CannotEditModal from './ContactInformationFieldInfo/CannotEditModal';
import ConfirmCancelModal from './ContactInformationFieldInfo/ConfirmCancelModal';
import ConfirmRemoveModal from './ContactInformationFieldInfo/ConfirmRemoveModal';
import UpdateSuccessAlert from './ContactInformationFieldInfo/ContactInformationUpdateSuccessAlert';
import ProfileInformationEditViewFc from './ProfileInformationEditViewFc';
import { ProfileInformationEditView2 } from './ProfileInformationEditViewFunctional';
import ProfileInformationView from './ProfileInformationView';

const wrapperClasses = prefixUtilityClasses([
  'display--flex',
  'flex-direction--column',
  'align-items--flex-start',
]);

const classes = {
  wrapper: wrapperClasses.join(' '),
  buttons:
    'vads-u-margin-bottom--1 vads-u-width--full mobile-lg:vads-u-width--auto',
};

export const ProfileInformationFieldController2 = ({
  fieldName,
  CustomConfirmCancelModal,
  allowInternationalPhones,
  ariaDescribedBy,
  cancelButtonText,
  cancelCallback,
  cancelWithoutConfirmation,
  forceEditView,
  isDeleteDisabled = false,
  prefillPatternEnabled,
  saveButtonText,
  showMailingAddressUpdateProfileChoice,
  successCallback,
  title,
}) => {
  const dispatch = useDispatch();

  // PORT NOTE: the class reached the router only through its withRouter-wrapped
  // export, so the two exports differed in whether subtask fields could
  // navigate. useHistory returns undefined outside a Router rather than
  // throwing, so one component covers both cases.
  const history = useHistory();

  const [showCannotEditModal, setShowCannotEditModal] = useState(false);
  const [showConfirmCancelModal, setShowConfirmCancelModal] = useState(false);
  const [shouldFocusCancelButton, setShouldFocusCancelButton] = useState(false);

  const {
    activeEditView,
    analyticsSectionName,
    apiRoute,
    blockEditMode,
    convertCleanDataToPayload,
    data,
    editViewData,
    email,
    formSchema,
    hasUnsavedEdits,
    homePhone,
    isEnrolledInVAHealthCare,
    mailingAddress,
    mobilePhone,
    showCopyAddressModal,
    showEditView,
    showErrorAlert,
    showRemoveModal,
    showUpdateSuccessAlert,
    showValidationView,
    title: fieldTitle,
    transaction,
    transactionError,
    transactionRequest,
    uiSchema,
    useVapSvcShowErrors,
    workPhone,
  } = useProfileInformationFieldData(fieldName, {
    title,
    allowInternationalPhones,
  });

  const closeModal = () => dispatch(openModal(null));

  // Clear errors on open to prevent showing an alert from a previous
  // transaction.
  const clearErrors = () => dispatch(clearTransactionRequest(fieldName));

  const applyEditStrategy = strategy => {
    switch (strategy.type) {
      case EDIT_STRATEGIES.SUBTASK_NAVIGATE:
        history.push(strategy.path);
        break;

      case EDIT_STRATEGIES.CANNOT_EDIT:
        setShowCannotEditModal(true);
        break;

      case EDIT_STRATEGIES.OPEN_EDIT_MODAL:
      case EDIT_STRATEGIES.OPEN_REMOVE_MODAL:
        if (strategy.clearErrors) {
          clearErrors();
        }
        dispatch(openModal(strategy.modal));
        break;

      /* c8 ignore next 2 */
      default:
        break;
    }
  };

  const onEdit = () =>
    applyEditStrategy(
      getEditStrategy({
        fieldName,
        blockEditMode,
        canNavigate: !!history,
      }),
    );

  const handleDeleteInitiated = () =>
    applyEditStrategy(getRemoveStrategy({ fieldName, blockEditMode }));

  const onDelete = () => {
    const strategy = getDeleteStrategy({
      fieldName,
      data,
      apiRoute,
      analyticsSectionName,
      convertCleanDataToPayload,
    });

    switch (strategy.type) {
      case DELETE_STRATEGIES.SCHEDULING_PREFERENCES:
        dispatch(createSchedulingPreferencesUpdate(strategy.request));
        break;

      case DELETE_STRATEGIES.MESSAGING_SIGNATURE:
        dispatch(
          updateMessagingSignature(
            strategy.payload,
            fieldName,
            strategy.method,
          ),
        );
        break;

      default:
        dispatch(
          createTransaction(
            apiRoute,
            strategy.method,
            fieldName,
            strategy.payload,
            analyticsSectionName,
          ),
        );
        break;
    }

    closeModal();
  };

  const confirmDeleteAction = event => {
    event.preventDefault();
    onDelete();
  };

  const onCancel = () => {
    if (!hasUnsavedEdits || cancelWithoutConfirmation) {
      closeModal();
      // cancel form app inline editing. Allows changing route
      if (typeof cancelCallback === 'function') {
        cancelCallback();
      }
      return;
    }

    setShowConfirmCancelModal(true);
  };

  const dismissConfirmCancelModal = () => {
    setShowConfirmCancelModal(false);
    setShouldFocusCancelButton(true);
  };

  const handleRefreshTransaction = () =>
    dispatch(refreshTransaction(transaction, analyticsSectionName));

  useFieldControllerEffects({
    fieldName,
    forceEditView,
    showEditView,
    showErrorAlert,
    showRemoveModal,
    showUpdateSuccessAlert,
    showValidationView,
    successCallback,
    transaction,
    transactionRequest,
    useVapSvcShowErrors,
    onCloseModal: closeModal,
  });

  // If the activeEditView is on the scheduling preferences page, use the
  // section heading for modals
  const activeSection = isSchedulingPreference(activeEditView)
    ? FIELD_SECTION_HEADERS[activeEditView]?.toLowerCase()
    : FIELD_TITLES[activeEditView]?.toLowerCase();

  const isLoading =
    transactionRequest?.isPending || isPendingTransaction(transaction);

  const wrapInTransaction = children => (
    <VAPServiceTransaction
      isModalOpen={
        showEditView ||
        showValidationView ||
        showRemoveModal ||
        forceEditView ||
        showCopyAddressModal
      }
      id={`${fieldName}-transaction-status`}
      title={fieldTitle}
      transaction={transaction}
      transactionRequest={transactionRequest}
      refreshTransaction={handleRefreshTransaction}
    >
      {children}
    </VAPServiceTransaction>
  );

  // default the content to the read-view
  let content = wrapInTransaction(
    <div className={classes.wrapper}>
      {showUpdateSuccessAlert && (
        <div className="vads-u-width--full">
          <UpdateSuccessAlert fieldName={fieldName} />
        </div>
      )}

      <ProfileInformationView
        data={data}
        fieldName={fieldName}
        title={fieldTitle}
        id={ariaDescribedBy}
        email={email}
        mailingAddress={mailingAddress}
        mobilePhone={mobilePhone}
        homePhone={homePhone}
        workPhone={workPhone}
      />
      <div className="vads-u-width--full">
        <div>
          {!isLoading && (
            <va-button
              text="Edit"
              label={`Edit ${fieldTitle}`}
              message-aria-describedby={ariaDescribedBy}
              onClick={onEdit}
              id={getEditButtonId(fieldName)}
              class={`vads-u-margin-top--1p5 ${classes.buttons}`}
              primary
            />
          )}
          {!!data &&
            !isLoading &&
            !isDeleteDisabled &&
            fieldName !== FIELD_NAMES.MAILING_ADDRESS && (
              <va-button
                text="Remove"
                label={`Remove ${fieldTitle}`}
                id={getRemoveButtonId(fieldName)}
                class={`vads-u-margin-top--1 ${classes.buttons}`}
                onClick={handleDeleteInitiated}
                secondary
              />
            )}
        </div>
      </div>
    </div>,
  );

  if (showEditView || forceEditView) {
    const editViewProps = getEditViewProps({
      allowInternationalPhones,
      apiRoute,
      cancelButtonText,
      convertCleanDataToPayload,
      data,
      editViewData,
      fieldName,
      forceEditView,
      formSchema,
      prefillPatternEnabled,
      saveButtonText,
      shouldFocusCancelButton,
      showMailingAddressUpdateProfileChoice,
      successCallback,
      title: fieldTitle,
      uiSchema,
      onCancel,
      onCancelButtonFocused: () => setShouldFocusCancelButton(false),
    });

    if (isPrefillMailingAddress({ fieldName, prefillPatternEnabled })) {
      content = <ProfileInformationEditViewFc {...editViewProps} />;
    } else {
      content = <ProfileInformationEditView2 {...editViewProps} />;
    }
  }

  if (showValidationView) {
    content = (
      <AddressValidationView2
        refreshTransaction={handleRefreshTransaction}
        transaction={transaction}
        transactionError={transactionError}
        title={fieldTitle}
        successCallback={successCallback}
      />
    );
  }

  return (
    <div data-field-name={fieldName} data-testid={fieldName}>
      {transactionError && (
        <VAPServiceEditModalErrorMessage error={transactionError} />
      )}

      {CustomConfirmCancelModal ? (
        <CustomConfirmCancelModal
          activeSection={activeSection}
          isVisible={showConfirmCancelModal}
          onHide={dismissConfirmCancelModal}
        />
      ) : (
        <ConfirmCancelModal
          activeSection={activeSection}
          closeModal={closeModal}
          onHide={dismissConfirmCancelModal}
          isVisible={showConfirmCancelModal}
        />
      )}

      <CannotEditModal
        activeSection={activeSection}
        onHide={() => setShowCannotEditModal(false)}
        isVisible={showCannotEditModal}
      />

      <ConfirmRemoveModal
        cancelAction={closeModal}
        deleteAction={confirmDeleteAction}
        title={fieldTitle}
        fieldName={fieldName}
        isEnrolledInVAHealthCare={isEnrolledInVAHealthCare}
        isVisible={showRemoveModal}
        onHide={closeModal}
        error={transactionError}
      />

      {content}
    </div>
  );
};

ProfileInformationFieldController2.propTypes = {
  fieldName: PropTypes.oneOf(Object.values(FIELD_NAMES)).isRequired,
  CustomConfirmCancelModal: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.node,
  ]),
  allowInternationalPhones: PropTypes.bool, // "opt-in" param for international phone numbers
  ariaDescribedBy: PropTypes.string,
  cancelButtonText: PropTypes.string,
  cancelCallback: PropTypes.func,
  cancelWithoutConfirmation: PropTypes.bool,
  forceEditView: PropTypes.bool,
  isDeleteDisabled: PropTypes.bool,
  prefillPatternEnabled: PropTypes.bool,
  saveButtonText: PropTypes.string,
  showMailingAddressUpdateProfileChoice: PropTypes.bool,
  successCallback: PropTypes.func,
  title: PropTypes.string,
};

export default ProfileInformationFieldController2;
